import {
  APICurrentUser,
  APIOrganizationUser,
  ArdoqId,
  OrgAccessLevel,
  PersonalSetting,
  PersonalSettingMap,
} from '@ardoq/api-types';
import { type PermissionContextUser } from '@ardoq/access-control';

/**
 * Check if the user is a service account user or normal user.
 */
const isServiceAccountUser = (user: APIOrganizationUser): boolean => {
  return user.serviceAccount;
};

const getUserRole = (user: PermissionContextUser) => user.organization.role;

/**
 * @deprecated Temporary fix while dishonest currentUser$ is being resolved
 */
const isUserFullyLoaded = (
  user: PermissionContextUser | null
): user is PermissionContextUser =>
  Boolean(user?._id && user.roles && user.organization?.label);

const hasRole = (user: PermissionContextUser, role: OrgAccessLevel) => {
  if (!isUserFullyLoaded(user)) return false;

  const orgLabel = user.organization.label;
  return (
    getUserRole(user) === role || user.roles.includes(`${orgLabel}/${role}`)
  );
};

const hasAdminRole = (user: PermissionContextUser) =>
  hasRole(user, OrgAccessLevel.ADMIN);

const hasWriterRole = (user: PermissionContextUser) =>
  hasRole(user, OrgAccessLevel.WRITER);

const hasReaderRole = (user: PermissionContextUser) =>
  hasRole(user, OrgAccessLevel.READER);

const hasOrgContributorRole = (user: PermissionContextUser) =>
  hasRole(user, OrgAccessLevel.CONTRIBUTOR);

/**
 * Check if the user has role that can write.
 * This is the same check as `user.writeAccess` set by the API.
 */
const hasWriterRolePlus = (user: PermissionContextUser) =>
  hasAdminRole(user) || hasWriterRole(user);

/**
 * Check if the user has role that can read data
 * Note: Contributors cannot read data, but can consume some resources
 */
const hasReaderRolePlus = (user: PermissionContextUser) =>
  hasAdminRole(user) || hasWriterRole(user) || hasReaderRole(user);

/**
 * Check if the current user has plain login enabled.
 */
const hasPlainLogin = (user: PermissionContextUser): boolean => {
  return Boolean(user.organization['custom-domain']?.['login-options']?.plain);
};

const isExpired = (user: PermissionContextUser) => {
  return user.organization.expired;
};

const hasAcceptedTerms = (user: APICurrentUser) => {
  return user['accepted-terms'];
};

const isArdoqEmployee = (user: PermissionContextUser): boolean =>
  user.email?.endsWith('@ardoq.com') ?? false;

const getOrgId = (user: PermissionContextUser): ArdoqId | null => {
  return user.organization?._id || null;
};

const getOrganization = (user: PermissionContextUser) => user.organization;

const getOrganizations = (user: APICurrentUser) => user.organizations;

const isTrial = (user: PermissionContextUser): boolean => {
  return user.organization?.['is-trial'] ?? false;
};

const isImpersonating = (user: APICurrentUser): boolean =>
  Boolean(user.impersonatedBy);

type KnownPersonalSettingKeys = keyof PersonalSettingMap;

function getPersonalSetting<T extends KnownPersonalSettingKeys>(
  user: APICurrentUser,
  key: T
): PersonalSettingMap[T];
function getPersonalSetting(
  user: APICurrentUser,
  key: string | PersonalSetting | KnownPersonalSettingKeys
): unknown {
  const settings = isUserFullyLoaded(user) ? user.settings : {};
  return settings[key as KnownPersonalSettingKeys] as unknown;
}

/**
 * Operations related to a single organization user.
 */
export const userOperations = {
  getOrgId,
  getOrganization,
  getOrganizations,
  getPersonalSetting,
  getUserRole,
  hasAcceptedTerms,
  isArdoqEmployee,
  isExpired,
  isImpersonating,
  isTrial,
  /**
   * Check if the user is a service account user or normal user.
   */
  isServiceAccountUser,
  isUserFullyLoaded,
  /**
   * Check if the current user has plain login enabled.
   */
  hasPlainLogin,
  hasWriterRolePlus,
  hasReaderRolePlus,
  hasAdminRole,
  hasWriterRole,
  hasReaderRole,
  hasOrgContributorRole,
};
